import React from 'react';

import { useParams } from 'react-router-dom';

import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import LimitationCard from 'src/components/shared/jmrCards/limitationCard';
import RestrictionCard from 'src/components/shared/jmrCards/restrictionCard';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { getAssociateProfileQueryOptions } from 'src/services/associateProfile/associateProfileQueries';
import type { AssociateProfileLimitation, AssociateProfileRestriction } from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';

import RestrictionHeader from './restrictionHeader';

const AssociateProfileRestrictions = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);

  const restrictionsQuery = useQuery(getAssociateProfileQueryOptions.restrictions(alias ?? '', selectedSite.id));
  const limitationsQuery = useQuery(getAssociateProfileQueryOptions.limitations(alias ?? '', selectedSite.id));

  const restrictionSort = (
    a: AssociateProfileRestriction | AssociateProfileLimitation,
    b: AssociateProfileRestriction | AssociateProfileLimitation,
  ) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  };

  if (restrictionsQuery.isPending || limitationsQuery.isPending) {
    return <LoadingSpinner />;
  }

  if (
    !restrictionsQuery.isError &&
    !limitationsQuery.isError &&
    restrictionsQuery.data.pending.length === 0 &&
    limitationsQuery.data.pending.length === 0 &&
    restrictionsQuery.data.active.length === 0 &&
    limitationsQuery.data.active.length === 0 &&
    restrictionsQuery.data.transferredIn.length === 0 &&
    limitationsQuery.data.transferredIn.length === 0
  ) {
    return <Text>There were no restrictions found for this Associate.</Text>;
  }

  const getTotalRestrictions = (isPending: boolean) => {
    if (restrictionsQuery.isError && limitationsQuery.isError) return null;

    if (isPending) {
      return `(${
        (!restrictionsQuery.isError
          ? restrictionsQuery.data.pending.length + restrictionsQuery.data.transferredIn.length
          : 0) +
        (!limitationsQuery.isError
          ? limitationsQuery.data.pending.length + limitationsQuery.data.transferredIn.length
          : 0)
      })`;
    } else {
      return `(${
        (!restrictionsQuery.isError ? restrictionsQuery.data.active.length : 0) +
        (!limitationsQuery.isError ? limitationsQuery.data.active.length : 0)
      })`;
    }
  };

  const formatEndDate = (date: string) => {
    const dateMoment = moment(date);
    return dateMoment.isValid() ? dateMoment.format('L') : '';
  };

  return (
    <Column spacing="400">
      <Column>
        <Text type="b500">Pending Restrictions {getTotalRestrictions(true)}</Text>
        {!restrictionsQuery.isError ? (
          [...restrictionsQuery.data.pending, ...restrictionsQuery.data.transferredIn]
            .sort(restrictionSort)
            .map((restriction) => (
              <RestrictionCard
                key={`r-${restriction.accommodationId}-${restriction.taskName}`}
                endAt={formatEndDate(restriction.endAt)}
                hoursRestricted={restriction.hoursRestricted}
                bodyLocation={restriction.bodyLocation}
                cardHeader={<RestrictionHeader {...restriction} />}
              />
            ))
        ) : (
          <Alert type="error">Oops! Something went wrong in loading Physical Restrictions.</Alert>
        )}

        {!limitationsQuery.isError ? (
          [...limitationsQuery.data.pending, ...limitationsQuery.data.transferredIn]
            .sort(restrictionSort)
            .map((limitation) => (
              <LimitationCard
                key={`l-${limitation.accommodationId}-${limitation.taskName}`}
                duration={limitation.duration}
                freeFormText={limitation.freeFormText}
                frequency={limitation.frequency}
                cardHeader={<RestrictionHeader {...limitation} isNonPhysical />}
              />
            ))
        ) : (
          <Alert type="error">Oops! Something went wrong in loading Non-Physical Limitations.</Alert>
        )}
      </Column>
      <Column>
        <Text type="b500">Active Restrictions {getTotalRestrictions(false)}</Text>
        {!restrictionsQuery.isError ? (
          <>
            {[...restrictionsQuery.data.active].sort(restrictionSort).map((restriction) => (
              <RestrictionCard
                key={`r-${restriction.accommodationId}-${restriction.taskName}`}
                endAt={formatEndDate(restriction.endAt)}
                hoursRestricted={restriction.hoursRestricted}
                bodyLocation={restriction.bodyLocation}
                cardHeader={<RestrictionHeader {...restriction} />}
              />
            ))}
          </>
        ) : (
          <Alert type="error">Oops! Something went wrong in loading Physical Restrictions.</Alert>
        )}

        {!limitationsQuery.isError ? (
          [...limitationsQuery.data.active]
            .sort(restrictionSort)
            .map((limitation) => (
              <LimitationCard
                key={`l-${limitation.accommodationId}-${limitation.taskName}`}
                duration={limitation.duration}
                freeFormText={limitation.freeFormText}
                frequency={limitation.frequency}
                cardHeader={<RestrictionHeader {...limitation} isNonPhysical />}
              />
            ))
        ) : (
          <Alert type="error">Oops! Something went wrong in loading Non-Physical Limitations.</Alert>
        )}
      </Column>
    </Column>
  );
};

export default AssociateProfileRestrictions;
